import type { TypedData, TypedDataDomain } from 'abitype';
import secp256k1 from 'secp256k1';
import { Hex, hexToNumber, Signature } from 'viem';

export interface EIP712TypedData {
  types: TypedData;
  domain: TypedDataDomain;
  message: {
    [key: string]: unknown;
  };
  primaryType: string;
}

export type SignatureExtended = Signature & {
  recoveryParam: number;
};

export enum SignatureType {
  Illegal = 0,
  Invalid = 1,
  EIP712 = 2,
  EthSign = 3,
  /** Raw signature bytes passed directly to EIP-1271 isValidSignature() for smart accounts */
  Raw = 5,
}

/**
 * Split a signature into its components using secp256k1-node
 * @param signatureHex - Hex string of the signature
 */
export function splitSignature(signatureHex: Hex): SignatureExtended {
  const hexLength = 64;

  // remove '0x' prefix and split into components
  const sigHex = signatureHex.slice(2);

  // extract and pad r and s components if needed
  const rHex = sigHex.slice(0, 64).padStart(hexLength, '0');
  const sHex = sigHex.slice(64, 128).padStart(hexLength, '0');
  const r = `0x${rHex}` as Hex;
  const s = `0x${sHex}` as Hex;
  const v = hexToNumber(`0x${sigHex.slice(128)}`);

  // convert hex strings to Uint8Array for secp256k1 validation
  const rBytes = Buffer.from(rHex, 'hex');
  const sBytes = Buffer.from(sHex, 'hex');

  // validate the signature components
  const sigObj = new Uint8Array([...rBytes, ...sBytes]);
  if (!secp256k1.signatureNormalize(sigObj)) throw new Error('Invalid signature');

  return {
    r,
    s,
    v: BigInt(v),
    recoveryParam: 1 - (v % 2),
  };
}
